import { BaseSaver } from './BaseSaver';
import { LocalStorageSaver } from './LocalStorageSaver';
import { Data, Logger } from './types';

export enum Privileges {
  NOT_PERMITTED = 0,
  PERMITTED = 1
}

type GetLatestData<T> = (dataBuffer: Partial<T>[]) => Partial<T> | null;
type HandleError = (err: Error) => void;

type SaveHandlerProps<T extends Data> = {
  drivers: BaseSaver<T>[];
  getLatestData: GetLatestData<T>;
  handleError: HandleError;
  logger: Logger;
}

export class SaveHandler<T extends Data> {
  private _priv: Privileges = Privileges.NOT_PERMITTED;
  private readonly _drivers: BaseSaver<T>[];
  private readonly getLatestData: GetLatestData<T>;
  private readonly handleError: HandleError;

  constructor(props: SaveHandlerProps<T>) {
    this._drivers = props.drivers;
    this.getLatestData = props.getLatestData;
    this.handleError = props.handleError;

    this._drivers.forEach((driver) => {
      driver.setLogger(props.logger);
    });
  }

  public setSavePrivileges(priv: Privileges): void {
    this._priv = priv;
  }

  public save(_data: Partial<T>): Promise<void> {
    if (this._priv !== Privileges.PERMITTED) {
      return Promise.reject(new Error('SaveLoader not allowed to save.'));
    }

    return new Promise<void>((resolve: Function, reject: Function) => {
      let handledPrimarySaver: boolean = false;

      for (let i: number = 0; i < this._drivers.length; i++) {
        const currentDriver: BaseSaver<T> = this._drivers[i];

        if (currentDriver.enabled) {
          if (!handledPrimarySaver) {
            handledPrimarySaver = true;

            currentDriver
              .save(_data)
              .then((_res) => resolve(_res))
              .catch(this.handleError);
          } else {
            currentDriver
              .save(_data)
              .catch(this.handleError);
          }
        }
      }

      if (!handledPrimarySaver) {
        // if we reach here, nothing was enabled
        reject(new Error('No saver instances were enabled!'));
      }
    });
  }

  public async load(_keys?: string[]): Promise<unknown> {
    const dataBuffer: T[] = [];

    for (let i: number = 0; i < this._drivers.length; i++) {
      let data: unknown;

      try {
        data = await this._drivers[i].load(_keys);
      } catch (err) {

        // if it's something that we actually care about failing
        if (!(this._drivers[i] instanceof LocalStorageSaver) && this._drivers.length !== 1) {
          throw err;
        } else {
          data = { data_timestamp: 1 };
        }
      }

      dataBuffer.push(data as T);
    }

    return this.getLatestData(dataBuffer) || {};
  }

  public async clear(): Promise<void> {
    for (let i: number = 0; i < this._drivers.length; i++) {
      await this._drivers[i]
        .clear()
        .catch((err: Error) => {
          // thorough error tracking not needed
          console.error(err);
        });
    }
  }
}
